"use client";

import { useEffect, useState } from "react";
import { getPublicSymbols, symbolLabel, type PublicSymbol } from "@/lib/api";
import { FOREX_TILE, SymbolIcon, symbolName } from "@/components/SymbolIcon";

/** THE MARKETS WE TRADE, on the landing page (owner 2026-09-25: "show symbol we trade also on
 *  frontend"). Read live from `GET /public/symbols` — the union of every master's `symbols`
 *  list, the same column the engine re-reads each tick — so the page can never advertise a pair
 *  no master trades. Nothing is painted until the read returns, and nothing at all if it returns
 *  empty: a hard-coded list here would be a second source of truth for what the platform trades.
 *
 *  Same marks as the dashboard's pair chips and the decision ticker (`SymbolIcon`), larger: the
 *  reader should recognise the row on the dashboard as the row they saw here. Each tile names
 *  the instrument in plain words, the contract it trades as, and which masters carry it. */
/** HOME PAGE ONLY (owner 2026-09-28: "show in frontend only home page replace BTCUSDT by BITCOIN
 *  ETHUSDT by ETHEREUM and SOLUSDT by SOLANA"). The visitor reads the market, not the venue's
 *  contract code. Display only — never sent anywhere; every other page keeps the real symbol. */
const HOME_TICKER: Record<string, string> = {
  BTCUSDT: "BITCOIN",
  ETHUSDT: "ETHEREUM",
  SOLUSDT: "SOLANA",
};

/** TRADED, BUT NOT SHOWN ON THE HOME PAGE (owner 2026-10-04; silver and GBPUSD added later,
 *  NEAR on 2026-10-12). The masters trade them and every other page lists them; only the landing
 *  tiles leave them out — GBPUSD is dropped before the Forex tile is built, so GBP is not among
 *  its currencies. */
const HOME_HIDDEN = new Set([
  "XRPUSDT",
  "BNBUSDT",
  "QQQUSDT",
  "SPYUSDT",
  "US30",
  "XAGUSDT",
  "GBPUSD",
  "NEARUSDT",
]);

function homeTicker(symbol: string): string {
  const named = HOME_TICKER[symbol.toUpperCase()];
  if (named) return named;
  return symbolLabel(symbol) === symbol ? symbol : `${symbolLabel(symbol)} · ${symbol}`;
}

/** FOREX IS ONE TILE ON THE HOME PAGE (owner 2026-10-02: "on home add only 1 not 3, 1 symbol with
 *  name forex, and where you write clusdt you add instead the 3 forex we have AUD EUR JPY"). The
 *  pairs the masters trade fold into a single "Forex" tile with the FX mark; its code line lists
 *  each pair's non-dollar currency (AUD · EUR · JPY), derived from the live list so it can never
 *  name a pair no master trades. Display only, home page only. */
const FOREX = /^(USD|EUR|JPY|AUD|GBP|CHF|CAD|NZD){2}$/;

type Tile = { key: string; icon: string; name: string; code: string; masters: string[] };

function tiles(rows: PublicSymbol[]): Tile[] {
  const out: Tile[] = [];
  let fx: Tile | null = null;
  for (const r of rows) {
    const sym = r.symbol.toUpperCase();
    if (HOME_HIDDEN.has(sym)) continue;
    // NAMED MARKETS ONLY: the stocks the masters trade carry no market name (their tile would read
    // its bare code, AAPLUSDT, ABBOTT), and dozens of them buried the six markets the page is about.
    if (!FOREX.test(sym) && symbolName(r.symbol).toUpperCase() === sym) continue;
    if (!FOREX.test(sym)) {
      out.push({ key: r.symbol, icon: r.symbol, name: symbolName(r.symbol), code: homeTicker(r.symbol), masters: r.masters });
      continue;
    }
    const ccy = sym.slice(0, 3) === "USD" ? sym.slice(3) : sym.slice(0, 3);
    if (!fx) {
      fx = { key: "FOREX", icon: FOREX_TILE, name: "Forex", code: "", masters: [] };
      out.push(fx);
    }
    const ccys = new Set(fx.code ? fx.code.split(" · ") : []);
    ccys.add(ccy);
    fx.code = [...ccys].sort().join(" · ");
    for (const m of r.masters) if (!fx.masters.includes(m)) fx.masters.push(m);
  }
  // THE FOREX TILE IS ALWAYS ON THE HOME PAGE (owner 2026-10-12): when no master lists a forex
  // pair yet (prod's masters carry crypto, gold and oil only), the tile still closes the row as it
  // does locally — AUD · EUR · JPY, the pairs the platform runs on cTrader, credited to every
  // master on the page. Home page only: no master's list and no other page change.
  if (!fx) {
    const masters = [...new Set(rows.flatMap((r) => r.masters))].sort();
    out.push({ key: "FOREX", icon: FOREX_TILE, name: "Forex", code: HOME_FOREX_CODE, masters });
  }
  return out;
}

/** The currencies the Forex tile names when no master lists a forex pair (EURUSD, AUDUSD,
 *  USDJPY: the pairs the platform runs on cTrader, GBPUSD left off the home page). */
const HOME_FOREX_CODE = "AUD · EUR · JPY";

export function MarketsStrip({ label, note }: { label: string; note: string }) {
  const [rows, setRows] = useState<PublicSymbol[] | null>(null);
  useEffect(() => {
    let alive = true;
    getPublicSymbols()
      .then((r) => alive && setRows(r.symbols))
      .catch(() => alive && setRows([]));
    return () => {
      alive = false;
    };
  }, []);
  if (!rows || rows.length === 0) return null;
  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="label mb-0!">{label}</span>
        <span className="text-xs text-muted">{note}</span>
      </div>
      {/* Never more than three across: the hero column is ~750px on a desktop and five tiles
          there truncated "Ethereum" and the masters line (measured at 1280px). Three keeps every
          name and every master legible; the fourth and fifth wrap to a second row. */}
      <ul className="mt-2.5 grid grid-cols-2 gap-2 sm:grid-cols-3">
        {tiles(rows).map((t) => (
          <li
            key={t.key}
            className="flex min-w-0 items-center gap-2.5 rounded-lg border border-border bg-bg-1/60 px-2.5 py-2"
            title={`${t.name} · ${t.code} · ${t.masters.join(", ")}`}
          >
            <SymbolIcon symbol={t.icon} size={28} />
            <div className="min-w-0 leading-tight">
              <div className="mark-yellow truncate text-sm font-semibold">{t.name}</div>
              <div className="truncate font-mono text-[11px] text-muted">{t.code}</div>
              {/* WRAPS, never truncates: on a phone the two-column tile cut "Intraday · Scalping ·
                  Swing" to "Intraday · Scalping ·…", hiding which masters carry the market — the
                  one thing this line is for (measured at 390px, 2026-10-03). */}
              <div className="text-[11px] text-muted">
                {t.masters.join(" · ")}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
